import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const API_URL = '/api';

export const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isLoading: false,
            error: null,

            login: async (email, password, twoFactorCode = null) => {
                set({ isLoading: true, error: null });
                console.log('[AuthStore] login called', { email, twoFactorCode: !!twoFactorCode });
                try {
                    const response = await fetch(`${API_URL}/auth/login`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, password, twoFactorCode })
                    });

                    const data = await response.json();
                    console.log('[AuthStore] response:', { ok: response.ok, requires2FA: data.requires2FA, hasToken: !!data.token });

                    // Handle 2FA challenge
                    if (data.requires2FA) {
                        set({ isLoading: false, error: null });
                        return {
                            requires2FA: true,
                            method: data.method,
                            message: data.message
                        };
                    }

                    if (!response.ok) {
                        throw new Error(data.error || 'Login failed');
                    }

                    set({
                        user: data.user,
                        token: data.token,
                        isLoading: false,
                        error: null
                    });

                    return true;
                } catch (error) {
                    set({ isLoading: false, error: error.message });
                    return false;
                }
            },

            logout: () => {
                set({ user: null, token: null, error: null });
            },

            setUser: (user) => {
                set({ user });
            },

            checkAuth: async () => {
                const { token } = get();
                if (!token) return false;

                try {
                    const response = await fetch(`${API_URL}/auth/me`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });

                    if (!response.ok) {
                        set({ user: null, token: null });
                        return false;
                    }

                    const user = await response.json();
                    set({ user });
                    return true;
                } catch {
                    set({ user: null, token: null });
                    return false;
                }
            },

            getAuthHeaders: () => {
                const { token } = get();
                return token ? { 'Authorization': `Bearer ${token}` } : {};
            },

            // Platform Owner = 'owner' or 'agency_admin' role (The Founder - has access to ALL businesses)
            isPlatformOwner: () => {
                const { user } = get();
                return user?.role === 'owner' || user?.role === 'agency_admin';
            },

            // isOwner kept for backwards compatibility - returns true for owner, agency_admin, or business_admin
            isOwner: () => {
                const { user } = get();
                return user?.role === 'owner' || user?.role === 'agency_admin' || user?.role === 'business_admin';
            },

            // For backwards compatibility with existing code that uses isAgencyAdmin
            isAgencyAdmin: () => {
                const { user } = get();
                return user?.role === 'owner' || user?.role === 'agency_admin'; // Platform owner
            },

            // Business Admin = 'business_admin' role (highest role for a specific business)
            isBusinessAdmin: () => {
                const { user } = get();
                return user?.role === 'business_admin';
            },

            // Demo mode check
            isDemo: () => {
                const { user } = get();
                return user?.role === 'demo';
            },

            // Demo login — instant access with email capture
            demoLogin: async (email) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await fetch(`${API_URL}/auth/demo`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: email || 'anonymous@demo.alwaysin.uk' })
                    });

                    const data = await response.json();
                    if (!response.ok) {
                        throw new Error(data.error || 'Demo login failed');
                    }

                    set({
                        user: data.user,
                        token: data.token,
                        isLoading: false,
                        error: null
                    });

                    return true;
                } catch (error) {
                    set({ isLoading: false, error: error.message });
                    return false;
                }
            }
        }),
        {
            name: 'alwaysin-auth',
            partialize: (state) => ({ token: state.token, user: state.user })
        }
    )
);
